import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import {
  formatearRNC,
  enmascararMientrasEscribe,
  limpiarRNC,
  esRNCValido,
  digitoVerificadorCorrecto,
} from '@/lib/rnc'
import { Boton } from '@/ui/Boton'
import { CampoTexto, CampoFormulario, Selector } from '@/ui/CampoFormulario'
import { InsigniaEstado, PuntoEstado } from '@/ui/InsigniaEstado'
import { RNC } from '@/ui/Numero'
import { PanelLateral } from '@/ui/PanelLateral'
import { TablaDatos } from '@/ui/tabla/TablaDatos'
import { useFiltrosEnUrl } from '@/ui/tabla/filtrosEnUrl'
import { traducirErrorApi } from '@/dominio/documentos/errores'
import {
  TIPOS_DOCUMENTO,
  nombreTipoDocumento,
  tipoDocumentoSugerido,
  type Contraparte,
  type TipoDocumentoContraparte,
} from '@/dominio/contrapartes/tipos'
import { useContrapartes, useCrearContraparte, useEditarContraparte } from '@/dominio/contrapartes/useContrapartes'
import { EncabezadoPantalla } from './-pantalla'

/**
 * Clientes y proveedores (E9).
 *
 * Tabla a pantalla completa, **alta en panel lateral y no en página nueva**:
 * el listado queda visible detrás.
 *
 * Tres cosas que esta pantalla enseñaba y no eran ciertas:
 *
 *   la columna «Tipo» pintaba `cliente` / `proveedor`, y el servidor manda el
 *   **tipo de documento** (`rnc`, `cedula`, `pasaporte`). El modelo del backend
 *   no distingue cliente de proveedor: lo decide el comprobante, no la ficha
 *
 *   las columnas «Última factura» y «Acumulado» no existen en el contrato, así
 *   que enseñaban un guion en todas las filas de todas las carteras
 *
 *   el filtro «Receptor: Electrónico» mandaba `receptorElectronico=si` contra un
 *   `bool?` y el servidor contestaba **500**. Un filtro de la barra que tumba la
 *   pantalla es peor que un filtro que no filtra
 */
export interface BusquedaClientes {
  nombre?: string
  identificacion?: string
  receptorElectronico?: string
}

export const Route = createFileRoute('/clientes')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaClientes => ({
    nombre: typeof busqueda.nombre === 'string' ? busqueda.nombre : undefined,
    identificacion: typeof busqueda.identificacion === 'string' ? busqueda.identificacion : undefined,
    receptorElectronico:
      typeof busqueda.receptorElectronico === 'string' ? busqueda.receptorElectronico : undefined,
  }),
  component: PantallaClientes,
})

function PantallaClientes() {
  const { filtros, aplicar, limpiar, hayFiltrosActivos } = useFiltrosEnUrl({
    nombre: '',
    identificacion: '',
    receptorElectronico: '',
  })

  const [pagina, setPagina] = useState(1)
  const [tamano, setTamano] = useState(50)
  const [orden, setOrden] = useState<SortingState>([{ id: 'nombre', desc: false }])
  const [enEdicion, setEnEdicion] = useState<Contraparte | 'nueva' | null>(null)

  const consulta = useContrapartes(
    {
      nombre: filtros.nombre === '' ? undefined : filtros.nombre,
      identificacion: filtros.identificacion === '' ? undefined : limpiarRNC(filtros.identificacion),
      // El parámetro es booleano. El texto de la URL se traduce aquí, que es la
      // frontera: lo que viaja al servidor tiene el tipo del contrato.
      receptorElectronico:
        filtros.receptorElectronico === '' ? undefined : filtros.receptorElectronico === 'si',
    },
    pagina,
    tamano,
  )

  const columnas = useMemo<ColumnDef<Contraparte, unknown>[]>(
    () => [
      {
        id: 'rnc',
        accessorFn: (fila) => fila.rnc ?? '',
        header: 'Identificación',
        meta: {
          etiqueta: 'Identificación',
          numerico: true,
          ancho: '150px',
          exportar: (f) => f.rncFormateado ?? f.rnc,
        },
        cell: ({ row }) =>
          row.original.rnc === null ? (
            // El consumidor final de un tipo 32 no tiene número, y eso es un
            // hecho del dominio, no un dato que falte.
            <span className="numero text-gris-400">sin identificación</span>
          ) : (
            <span className="flex items-center justify-end gap-1">
              <RNC valor={row.original.rnc} />
              {/*
                El dígito verificador **avisa, no bloquea** (regla 8). Hay
                contribuyentes activos cuyo número no lo pasa, y quien manda es
                el directorio de DGII: aquí es una marca, nunca un rechazo.
              */}
              {row.original.digitoVerificadorAnomalo ? (
                <span title="El dígito verificador no cuadra. Puede estar bien igual: quien manda es el directorio de DGII.">
                  <PuntoEstado tono="ambar" />
                </span>
              ) : null}
            </span>
          ),
      },
      {
        id: 'nombre',
        accessorFn: (fila) => fila.nombre,
        header: 'Nombre',
        meta: { etiqueta: 'Nombre', exportar: (f) => f.nombre },
        cell: ({ row }) => (
          <span className="block truncate text-gris-900" title={row.original.nombre}>
            {row.original.nombre}
          </span>
        ),
      },
      {
        id: 'tipo',
        accessorFn: (fila) => fila.tipo,
        header: 'Documento',
        meta: { etiqueta: 'Documento', ancho: '150px', exportar: (f) => nombreTipoDocumento(f.tipo) },
        cell: ({ row }) => <span className="text-gris-700">{nombreTipoDocumento(row.original.tipo)}</span>,
      },
      {
        id: 'pais',
        accessorFn: (fila) => fila.pais,
        header: 'País',
        meta: { etiqueta: 'País', ancho: '80px', exportar: (f) => f.pais },
        cell: ({ row }) => <span className="mono text-gris-600">{row.original.pais}</span>,
      },
      {
        id: 'receptor',
        accessorFn: (fila) => fila.receptorElectronico,
        header: 'Receptor electrónico',
        enableSorting: false,
        meta: {
          etiqueta: 'Receptor electrónico',
          ancho: '190px',
          exportar: (f) => (f.receptorElectronico ? 'sí' : 'no'),
        },
        // Punto verde o gris con texto, alimentado por el directorio (E24).
        cell: ({ row }) =>
          row.original.receptorElectronico ? (
            <InsigniaEstado tono="verde">electrónico</InsigniaEstado>
          ) : (
            <InsigniaEstado tono="neutral">no electrónico</InsigniaEstado>
          ),
      },
      {
        id: 'estado',
        accessorFn: (fila) => (fila.activa ? 'activa' : 'anulada'),
        header: 'Estado',
        enableSorting: false,
        meta: { etiqueta: 'Estado', ancho: '110px', exportar: (f) => (f.activa ? 'activa' : 'anulada') },
        cell: ({ row }) =>
          row.original.activa ? (
            <InsigniaEstado tono="verde">Activa</InsigniaEstado>
          ) : (
            <InsigniaEstado tono="neutral">Anulada</InsigniaEstado>
          ),
      },
    ],
    [],
  )

  const errorTraducido = consulta.isError ? traducirErrorApi(consulta.error) : null

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <EncabezadoPantalla
        titulo="Clientes y proveedores"
        acciones={
          <Boton variante="primario" icono={<Plus />} onClick={() => setEnEdicion('nueva')}>
            Nuevo
          </Boton>
        }
      />

      <TablaDatos<Contraparte>
        idTabla="contrapartes"
        columnas={columnas}
        datos={consulta.data?.filas ?? []}
        obtenerId={(fila) => fila.id}
        cargando={consulta.isLoading}
        error={
          errorTraducido === null
            ? null
            : {
                titulo: errorTraducido.titulo,
                contexto: errorTraducido.contexto,
                detalleTecnico: errorTraducido.detalleTecnico,
              }
        }
        onReintentar={() => void consulta.refetch()}
        vacio={{
          variante: 'primera-vez',
          mensaje: 'Aún no tienes clientes ni proveedores.',
          accion: (
            <Boton variante="primario" icono={<Plus />} onClick={() => setEnEdicion('nueva')}>
              Nuevo
            </Boton>
          ),
        }}
        vacioFiltrado={{
          variante: 'sin-resultados',
          mensaje: 'Ninguna contraparte con los filtros aplicados.',
          accion: (
            <Boton variante="secundario" tamano="menor" onClick={limpiar}>
              Limpiar filtros
            </Boton>
          ),
        }}
        orden={orden}
        onOrdenChange={setOrden}
        paginacion={{ pagina, tamano, total: consulta.data?.total ?? 0 }}
        onPaginacionChange={(siguiente) => {
          setPagina(siguiente.pagina)
          setTamano(siguiente.tamano)
        }}
        nombreExportacion="contrapartes"
        hayFiltrosActivos={hayFiltrosActivos}
        onLimpiarFiltros={limpiar}
        filtros={
          <>
            {/*
              Dos campos y no uno: el extremo busca por `nombre` **o** por
              `identificacion`, en parámetros distintos. Un solo buscador que
              adivinara cuál usar acierta casi siempre y falla justo con la
              ferretería que se llama «100».
            */}
            <input
              type="search"
              value={filtros.nombre}
              onChange={(evento) => aplicar({ nombre: evento.target.value })}
              placeholder="Nombre o razón social"
              aria-label="Buscar contrapartes por nombre"
              className="h-[var(--alto-control-menor)] w-[240px] rounded-md border border-gris-500 bg-white px-2 text-cuerpo text-gris-900"
            />
            <input
              type="search"
              value={filtros.identificacion}
              onChange={(evento) => aplicar({ identificacion: evento.target.value })}
              placeholder="RNC o cédula"
              aria-label="Buscar contrapartes por identificación"
              className="numero h-[var(--alto-control-menor)] w-[160px] rounded-md border border-gris-500 bg-white px-2 text-gris-900"
            />
            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Receptor
              <select
                value={filtros.receptorElectronico}
                onChange={(evento) => aplicar({ receptorElectronico: evento.target.value })}
                className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
              >
                <option value="">Todos</option>
                <option value="si">Electrónico</option>
                <option value="no">No electrónico</option>
              </select>
            </label>
          </>
        }
        onFilaClick={(fila) => setEnEdicion(fila)}
      />

      <PanelDeContraparte contraparte={enEdicion} onCerrar={() => setEnEdicion(null)} />
    </div>
  )
}

/* ---------------------------------------------------------------------- */

function PanelDeContraparte({
  contraparte,
  onCerrar,
}: {
  contraparte: Contraparte | 'nueva' | null
  onCerrar: () => void
}) {
  const esNueva = contraparte === 'nueva'
  const existente = contraparte === 'nueva' || contraparte === null ? null : contraparte

  const [rnc, setRnc] = useState('')
  const [nombre, setNombre] = useState('')
  const [tipoDocumento, setTipoDocumento] = useState<TipoDocumentoContraparte>('rnc')
  const [pais, setPais] = useState('DO')

  const crear = useCrearContraparte()
  const editar = useEditarContraparte()
  const enCurso = crear.isPending || editar.isPending

  useEffect(() => {
    if (contraparte === null) return
    setRnc(existente === null ? '' : formatearRNC(existente.rnc))
    setNombre(existente?.nombre ?? '')
    setTipoDocumento((existente?.tipo as TipoDocumentoContraparte) ?? 'rnc')
    setPais(existente?.pais ?? 'DO')
    crear.reset()
    editar.reset()
    // Solo al abrir el panel con otra contraparte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contraparte])

  const digitos = limpiarRNC(rnc)
  const exigeNumero = tipoDocumento !== 'sin_identificacion'
  const estructuraValida = !exigeNumero || esRNCValido(digitos)
  const avisoDigito = digitoVerificadorCorrecto(digitos) === false

  const error = crear.isError
    ? traducirErrorApi(crear.error)
    : editar.isError
      ? traducirErrorApi(editar.error)
      : null

  const guardar = (): void => {
    if (existente !== null) {
      // El servidor solo deja cambiar el nombre y el país: la identificación no
      // se edita, porque cambiarla convertiría la ficha en la de otro
      // contribuyente arrastrando el histórico ya emitido.
      editar.mutate({ id: existente.id, nombre: nombre.trim(), pais }, { onSuccess: onCerrar })
      return
    }
    crear.mutate(
      {
        tipoDocumento,
        ...(exigeNumero ? { numeroIdentificacion: digitos } : {}),
        nombre: nombre.trim(),
        pais,
      },
      { onSuccess: onCerrar },
    )
  }

  return (
    <PanelLateral
      abierto={contraparte !== null}
      onCerrar={onCerrar}
      titulo={esNueva ? 'Nueva contraparte' : (existente?.nombre ?? 'Contraparte')}
      subtitulo={existente !== null ? (existente.rncFormateado ?? formatearRNC(existente.rnc)) : undefined}
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            variante="primario"
            disabled={!estructuraValida || nombre.trim() === '' || enCurso}
            onClick={guardar}
          >
            {enCurso ? 'Guardando…' : 'Guardar'}
          </Boton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error !== null ? <p className="text-cuerpo text-rojo">{error.titulo}</p> : null}

        <CampoFormulario etiqueta="Tipo de documento" obligatorio>
          {(atributos) => (
            <Selector
              {...atributos}
              value={tipoDocumento}
              disabled={existente !== null}
              onChange={(evento) => setTipoDocumento(evento.target.value as TipoDocumentoContraparte)}
            >
              {(Object.keys(TIPOS_DOCUMENTO) as TipoDocumentoContraparte[]).map((valor) => (
                <option key={valor} value={valor}>
                  {TIPOS_DOCUMENTO[valor]}
                </option>
              ))}
            </Selector>
          )}
        </CampoFormulario>

        {exigeNumero ? (
          <CampoTexto
            etiqueta="Número de identificación"
            obligatorio
            value={rnc}
            inputMode="numeric"
            numerico
            disabled={existente !== null}
            // Máscara progresiva mientras se teclea: 1318 → 1-31-8.
            onChange={(evento) => {
              const texto = enmascararMientrasEscribe(evento.target.value)
              setRnc(texto)
              // Sugerencia de captura, no validación: nueve dígitos son un RNC
              // y once una cédula. El usuario puede cambiarla.
              const sugerido = tipoDocumentoSugerido(limpiarRNC(texto))
              if (sugerido !== null) setTipoDocumento(sugerido)
            }}
            error={rnc !== '' && !estructuraValida ? 'Deben ser 9 dígitos, u 11 si es cédula.' : undefined}
            ayuda={existente !== null ? 'No se puede cambiar: daría de alta a otro contribuyente.' : undefined}
          />
        ) : null}

        {/*
          El dígito verificador **avisa, no bloquea** (regla 8). Va aparte del
          error del campo, y con otro tono, porque no es un error: hay
          contribuyentes activos cuyo número no pasa el algoritmo, y el servidor
          los registra igual devolviendo el aviso.
        */}
        {avisoDigito ? (
          <p className="flex items-start gap-2 text-etiqueta text-ambar">
            <PuntoEstado tono="ambar" className="mt-[6px]" />
            <span>
              El dígito verificador no cuadra. Puede estar bien igual — quien manda es el directorio
              de DGII. No impide guardar.
            </span>
          </p>
        ) : null}

        <CampoTexto
          etiqueta="Nombre o razón social"
          obligatorio
          value={nombre}
          onChange={(evento) => setNombre(evento.target.value)}
          ayuda="Como consta en el RNC."
        />

        <CampoTexto
          etiqueta="País"
          value={pais}
          onChange={(evento) => setPais(evento.target.value.toUpperCase().slice(0, 2))}
          ayuda="Código de dos letras. DO salvo en exportaciones y pagos al exterior."
        />

        {/*
          Aquí había un campo de correo «a donde se envía la representación
          impresa». No se enviaba a ninguna parte: el contrato de alta no lo
          tiene y el servidor lo descartaba en silencio. Un campo que se rellena
          y se pierde es peor que un campo que falta, porque el usuario cree que
          el envío está configurado.
        */}
        <p className="text-etiqueta text-gris-600">
          El correo de envío y la consulta al directorio de DGII todavía no existen en el servidor.
          Cuando existan, entran aquí.
        </p>
      </div>
    </PanelLateral>
  )
}
